import { useContext, useEffect, useMemo, useState } from "react";
import AuthClient from "../api/AuthClient";
import OpenClubsForPromoterGroupApi from "../api/OpenClubsForPromoterGroupApi";
import { parseOpenClubsResponse } from "../api/parseResponse/parsePromoterGroupOpenClubsResponse";
import { OpenClubsContext } from "../GlobalContext/openClubsContext";
import { OpenClubs, PartyModel } from "../types";

export type OpenClubsRequestState = "idle" | "loading" | "success" | "error";

const inFlightOpenClubsRequests = new Map<string, Promise<OpenClubs[]>>();

export const fetchPromoterGroupOpenClubs = async (
  group: PartyModel,
  bookingType = group.party.bookingType.toString()
): Promise<OpenClubs[]> => {
  const requestKey = `${group.party.id}:${bookingType}`;
  const existingRequest = inFlightOpenClubsRequests.get(requestKey);

  if (existingRequest) {
    return existingRequest;
  }

  const request = (async () => {
    const api = new OpenClubsForPromoterGroupApi(new AuthClient());
    const response = await api.getPromoterGroupOpenClubs(
      group.party.city,
      group.party.partyDate,
      bookingType,
      group.promotersOffers[0]?.promoter?.id ?? ""
    );

    return parseOpenClubsResponse(response?.data?.openClubs).openClubs ?? [];
  })();

  inFlightOpenClubsRequests.set(requestKey, request);

  try {
    return await request;
  } finally {
    inFlightOpenClubsRequests.delete(requestKey);
  }
};

export const usePromoterGroupOpenClubs = (
  group: PartyModel | null,
  bookingType?: string
) => {
  const { openClubs, setPromoterGroupOpenClubs } = useContext(OpenClubsContext);
  const [requestState, setRequestState] = useState<OpenClubsRequestState>("idle");

  useEffect(() => {
    if (!group) {
      setRequestState("idle");
      setPromoterGroupOpenClubs([]);
      return;
    }

    let isCancelled = false;

    const loadOpenClubs = async () => {
      setRequestState("loading");
      setPromoterGroupOpenClubs([]);

      try {
        const parsedOpenClubs = await fetchPromoterGroupOpenClubs(group, bookingType);

        if (isCancelled) {
          return;
        }

        setPromoterGroupOpenClubs(parsedOpenClubs);
        setRequestState("success");
      } catch (error) {
        if (!isCancelled) {
          console.error("Error fetching promoter open clubs:", error);
          setPromoterGroupOpenClubs([]);
          setRequestState("error");
        }
      }
    };

    void loadOpenClubs();

    return () => {
      isCancelled = true;
    };
  }, [bookingType, group, setPromoterGroupOpenClubs]);

  return useMemo(() => ({
    openClubs,
    requestState,
  }), [openClubs, requestState]);
};
